// Thin per-field derivations for a place row, all small, pure, and used only
// by PlaceListModule.

// --- geo ------------------------------------------------------------------

// Convert a Google-style geo ({lat, lng}) into the stored place geo shape ({lat, lon}).
export function toGeo(geo: { lat: number; lng: number }): { lat: number; lon: number } {
  return { lat: geo.lat, lon: geo.lng };
}

// --- name -----------------------------------------------------------------

// Normalize a place name for fuzzy matching: lowercase, strip diacritics,
// and drop a leading French article / "chez". The two call sites in
// PlaceListModule genuinely diverge on whether the English article "the"
// is also stripped:
//   - The chat "open [place name]" listener strips "the " (stripEnglishArticle: true).
//   - The cross-module object-selection listener does NOT (stripEnglishArticle: false).
// This divergence is preserved deliberately (rule 10) — do NOT silently unify.
// A reviewer may later decide whether the difference is a latent bug.
export function normalizePlaceName(
  s: string,
  { stripEnglishArticle }: { stripEnglishArticle: boolean },
): string {
  const articles = stripEnglishArticle
    ? /^(le |la |l'|les |chez |the )/i
    : /^(le |la |l'|les |chez )/i;
  return s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(articles, '');
}

// --- photos ---------------------------------------------------------------

// Picks the first real photo URL for a row thumbnail. Skips map/streetview
// fallbacks (pin-on-map images) attached by import/enrichment when no real
// photo is available — those should render as skeletons, not pins.
export function isMapFallbackUrl(url: string | undefined): boolean {
  if (!url) return false;
  return (
    url.includes('type=staticmap') ||
    url.includes('type=streetview') ||
    url.includes('maps.googleapis.com/maps/api/staticmap') ||
    url.includes('maps.googleapis.com/maps/api/streetview')
  );
}

export function pickRowPhotoUrl(place: any): string | undefined {
  const realFromArray = (place.images ?? []).find(
    (img: any) => img?.url && !img.isMap && !isMapFallbackUrl(img.url),
  )?.url;
  if (realFromArray) return realFromArray;
  return isMapFallbackUrl(place.imageUrl) ? undefined : place.imageUrl;
}
